import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, RefreshCw, ExternalLink, Copy, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import '../sponsor-manager/SponsorManager.css'
import './AdBuilder.css'

// Public render is served by the watch worker — see the plan at
// ~/.claude/plans/i-have-a-970x250-dynamic-lynx.md. Vader admin UI
// links out to the watch URL for embedding.
const WATCH_BASE = 'https://watch.tenband.tv'

// List view for ad-builder configs. Sibling to /sponsors/manager —
// each row is one configured 970x250 render; clicking opens the editor.
export default function AdBuilder() {
  const { api } = useAuth()
  const navigate = useNavigate()
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/ad-builder')
      setConfigs(data.configs || [])
      setStatus('')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api])

  useEffect(() => { load() }, [load])

  function renderUrlFor(slug) {
    return `${WATCH_BASE}/ad-builder/${slug}`
  }

  async function copyUrl(slug) {
    try {
      await navigator.clipboard.writeText(renderUrlFor(slug))
      setStatus(`Copied render URL for "${slug}"`)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Ad Builder</h1>
          <span className="sponsors-count">{configs.length}</span>
        </div>
        <div className="sponsors-header-right">
          <button className="sponsors-refresh-btn" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/sponsors/ad-builder/new')}>
            <Plus size={14} /> New ad page
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <p className="muted" style={{ marginTop: 4, marginBottom: 14, fontSize: '0.82rem' }}>
        Configures 970×250 ad pages served from{' '}
        <code>{WATCH_BASE}/ad-builder/&lt;slug&gt;</code>. The render URL is fully
        public — embed it in vMix browser inputs, OBS browser sources, or iframes.
      </p>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : configs.length === 0 ? (
        <div className="muted">
          No ad pages configured yet. Click <strong>New ad page</strong> to create one.
        </div>
      ) : (
        <table className="zones-table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Name</th>
              <th>Mode</th>
              <th>Zone</th>
              <th>Rotation</th>
              <th>vMix</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {configs.map(c => (
              <tr
                key={c.slug}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/sponsors/ad-builder/${c.slug}`)}
              >
                <td><code>{c.slug}</code></td>
                <td>{c.name}</td>
                <td>{c.mode === 'js' ? 'JS tag' : 'VAST'}</td>
                <td>{c.zone_id}</td>
                <td>{c.rotation_seconds}s</td>
                <td>{c.vmix_enabled ? `ov ${c.vmix_overlay ?? '?'}` : '—'}</td>
                <td className="muted" style={{ fontSize: '0.78rem' }}>
                  {c.updated_at?.replace('T', ' ').slice(0, 16) || ''}
                </td>
                <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  <button
                    className="sponsors-refresh-btn"
                    onClick={() => copyUrl(c.slug)}
                    title="Copy render URL"
                    style={{ fontSize: '0.78rem', padding: '3px 8px', marginRight: 4 }}
                  >
                    <Copy size={12} /> URL
                  </button>
                  <a
                    className="sponsors-refresh-btn"
                    href={renderUrlFor(c.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.78rem', padding: '3px 8px' }}
                  >
                    <ExternalLink size={12} /> Open
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
